class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Removed: this.header.reveal() — this forced the header into its
    // compact "scrolled" state (shrinking height, hiding the search bar)
    // every time an item was added to cart, regardless of actual scroll
    // position. That's Dawn's original default behavior, but it looked
    // like a bug with this site's custom header, which shrinks far more
    // dramatically than Dawn's stock header does. The notification still
    // opens normally; the header just stays as-is instead of collapsing.

    this.openUnlessRebuyTakesOver();
  }

  // DINKYDOO — Rebuy's "Smart Cart" flyout (the app embed that renders our
  // add-to-cart popup) shows its own cart popup roughly 400-700ms after an
  // add-to-cart request finishes. Dawn used to call open() immediately every
  // time, so Dawn's own notification flashed on screen for that gap before
  // Rebuy's popup covered it up — a jarring double-popup. We now give Rebuy a
  // short window to show its own flyout (document.body gets class
  // "rebuy-cart-visible" once it's open) and skip Dawn's popup when it does.
  // If Rebuy isn't installed, hasn't finished loading yet, or fails to show
  // its flyout in time, we fall back to Dawn's normal popup so the customer
  // always gets an add-to-cart confirmation either way.
  openUnlessRebuyTakesOver() {
    if (this.rebuyPollHandle) {
      cancelAnimationFrame(this.rebuyPollHandle);
      this.rebuyPollHandle = null;
    }

    if (typeof window.Rebuy === 'undefined') {
      this.open();
      return;
    }

    if (document.body.classList.contains('rebuy-cart-visible')) {
      // Rebuy's flyout is already open (e.g. a fast repeat add) — don't
      // stack Dawn's popup on top of it.
      return;
    }

    const REBUY_GRACE_MS = 1200;
    const start = performance.now();
    const poll = () => {
      if (document.body.classList.contains('rebuy-cart-visible')) {
        this.rebuyPollHandle = null;
        return;
      }
      if (performance.now() - start >= REBUY_GRACE_MS) {
        this.rebuyPollHandle = null;
        this.open();
        return;
      }
      this.rebuyPollHandle = requestAnimationFrame(poll);
    };
    poll();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
